import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import '../../icons/spin';

type buttonMode =
  | 'primary'
  | 'dashed'
  | 'danger'
  | 'link'
  | 'text'
  | 'round'
  | 'circle'
  | 'block';

@customElement('wc-button')
export class GSMLGButton extends LitElement {
  static override styles = css`
    :root {
      font-size: inherit;
      box-sizing: border-box;
    }

    .btn {
      line-height: 1.5715;
      position: relative;
      display: inline-flex;
      justify-content: center;
      align-items: center;
      font-weight: 400;
      white-space: nowrap;
      background-image: none;
      border: 1px solid transparent;
      box-shadow: 0 2px 0 rgba(0, 0, 0, 0.015);
      cursor: pointer;
      transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
      user-select: none;
      touch-action: manipulation;
      height: 2.25rem;
      padding: 0.3rem 1.1rem;
      font-size: 1rem;
      border-radius: 0.2rem;
      color: rgba(0, 0, 0, 0.85);
      border-color: var(--btn-border, #d9d9d9);
      background: #fff;
    }
    a.btn {
      text-decoration: none;
    }
    .btn icon-spin {
      margin-left: -0.4rem;
      margin-right: 0.4rem;
    }
    .btn.btn-primary {
      color: #fff;
      border-color: var(--btn-primary, #1890ff);
      background: var(--btn-primary, #1890ff);
    }
    .btn.btn-dashed {
      border: 1px dashed transparent;
      background: transparent;
      border-color: rgba(0, 0, 0, 0.85);
    }
    .btn.btn-danger {
      color: rgb(255, 255, 255);
      border-color: var(--btn-danger, #ff4d4f);
      background: var(--btn-danger, #ff4d4f);
      text-shadow: rgb(0 0 0 / 12%) 0px -1px 0px;
      box-shadow: rgb(0 0 0 / 4%) 0px 2px 0px;
    }
    .btn.btn-link,
    .btn.btn-text {
      color: rgba(0, 0, 0, 0.85);
      border: none;
      background: transparent;
      text-shadow: rgb(0 0 0 / 12%) 0px -1px 0px;
      box-shadow: none;
    }
    .btn.btn-link {
      color: var(--btn-link, #1890ff);
    }
    .btn.btn-round {
      height: 2.25rem;
      padding: 0.3rem 1.1rem;
      font-size: 1rem;
      border-radius: 2.25rem;
    }
    .btn.btn-circle {
      height: 2.25rem;
      width: 2.25rem;
      padding: 0.3rem 1.1rem;
      font-size: 1rem;
      border-radius: 50%;
    }
    .btn.btn-circle icon-spin {
      position: absolute;
      margin: 0;
    }
    .btn.btn-block {
      height: 2.25rem;
      width: 100%;
      padding: 0.3rem 1.1rem;
      font-size: 1rem;
    }

    .btn:hover {
      color: var(--btn-hover, #40a9ff);
      border-color: var(--btn-hover, #40a9ff);
    }
    .btn.btn-primary:hover {
      color: #fff;
      background: var(--btn-primary-hover, #40a9ff);
      border-color: var(--btn-primary-hover, #40a9ff);
    }
    .btn.btn-danger:hover {
      color: #fff;
      background: var(--btn-danger-hover, #ff7875);
      border-color: var(--btn-danger-hover, #ff7875);
    }
    .btn[disabled] {
      cursor: not-allowed;
    }
    .btn[disabled].btn-loading {
      cursor: progress;
    }
    .btn[disabled]:not(.btn-loading),
    .btn[disabled].disabled {
      color: rgba(0, 0, 0, 0.25);
      background: var(--btn-disabled, #f5f5f5);
      broder-color: var(--btn-border-disabled, #d9d9d9);
      border: none;
    }
  `;

  @property({ type: String, reflect: true, attribute: true })
  href: string | undefined = undefined;

  @property({ type: String, reflect: true, attribute: true })
  target: string | undefined = undefined;

  @property({ type: String, reflect: true, attribute: true })
  name: string = '';

  @property({ type: String, reflect: true, attribute: true })
  mode: buttonMode | '' = '';

  @property({ type: String, reflect: true, attribute: true })
  type: string = '';

  @property({ type: Boolean, reflect: true, attribute: true })
  disabled: boolean = false;

  @property({ type: Boolean, reflect: true, attribute: true })
  loading: boolean = false;

  override connectedCallback() {
    super.connectedCallback();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
  }

  override attributeChangedCallback(
    name: string,
    old: string | null,
    value: string | null,
  ) {
    super.attributeChangedCallback(name, old, value);
    this.requestUpdate();
  }

  override render() {
    const size = parseInt(getComputedStyle(this).fontSize, 10);

    if (this.href !== undefined) {
      return html`
        <a
          href=${this.href}
          class=${this.getClass()}
          target=${this.target}
          ?disabled=${this.disabled || this.loading}
        >
          ${this.loading
            ? html`<icon-spin size=${this.mode === 'circle' ? size * 2.3 : size} />`
            : ''}
          <slot></slot>
        </a>
      `;
    }

    return html`
      <button
        type=${this.type}
        class=${this.getClass()}
        ?disabled=${this.disabled || this.loading}
      >
        ${this.loading
          ? html`<icon-spin size=${this.mode === 'circle' ? size * 2.3 : size} />`
          : ''}
        <slot></slot>
      </button>
    `;
  }

  private getClass() {
    const isLink = this.href !== undefined;
    return `btn ${
      this.mode !== '' ? `btn-${this.mode}` : `${isLink ? 'btn-link' : ''}`
    } ${this.disabled ? 'disabled' : ''} ${this.loading ? 'btn-loading' : ''}`;
  }
}
